/**
 * ShippingPage — DHL shipping label creation for orders in "Druck" stage.
 *
 * Flow:
 *   Step 1 (select)  — checkbox list of "Druck" orders; pick which to ship
 *   Step 2 (review)  — verify/edit address + choose weight per order
 *   Step 3 (done)    — tracking numbers displayed; download combined PDF
 *
 * Labels are created via the `createShippingLabels` Cloud Function (DHL API v2).
 * PDFs are merged client-side using pdf-lib and downloaded as one file.
 */
import React, { useEffect, useState } from "react";
import {
  collection, query, where, getDocs, doc, getDoc, setDoc,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { PDFDocument } from "pdf-lib";
import { db } from "../firebase";
import { SHOP } from "../shop";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function splitStreet(combined = "") {
  const m = combined.trim().match(/^(.+?)\s+(\d+\s*[a-zA-Z]?)$/);
  return m
    ? { street: m[1].trim(), house: m[2].trim() }
    : { street: combined.trim(), house: "" };
}

function suggestWeight(items = []) {
  if (items.some((i) => /kerze/i.test(i.title || ""))) return 2;
  if (items.some((i) => /g[äa]ste|stammbuch/i.test(i.title || ""))) return 5;
  return 2;
}

function formatDate(iso) {
  if (!iso) return "–";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
}

function formatScheduled(ts) {
  if (!ts) return "–";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

const WEIGHTS = [0.5, 1, 2, 5, 10, 31.5];

const card = {
  background: "#fff",
  borderRadius: "12px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  padding: "1.5em 2em",
  marginBottom: "1.5em",
};

const inputStyle = {
  padding: "0.35em 0.6em",
  border: "1px solid #cbd5e0",
  borderRadius: "6px",
  fontSize: "0.85rem",
  width: "100%",
  boxSizing: "border-box",
};

const btnPrimary = {
  background: "#553c9a", color: "#fff",
  border: "none", borderRadius: "8px",
  padding: "0.6em 1.4em", cursor: "pointer",
  fontWeight: "600", fontSize: "0.9rem",
};

const btnSecondary = {
  background: "transparent", color: "#553c9a",
  border: "1px solid #553c9a", borderRadius: "8px",
  padding: "0.6em 1.4em", cursor: "pointer",
  fontWeight: "600", fontSize: "0.9rem",
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function ShippingPage() {
  const [step, setStep] = useState("select"); // "select" | "review" | "done"
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduledDate, setScheduledDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [scheduledTime, setScheduledTime] = useState("09:00");
  const [selected, setSelected] = useState(new Set());
  const [shipRows, setShipRows] = useState([]); // rows for step 2
  const [searchQuery, setSearchQuery] = useState("");
  const [labelFilter, setLabelFilter] = useState("all"); // "all" | "no-label"
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [sandboxMode, setSandboxMode] = useState(false);
  const [shipQueueErrors, setShipQueueErrors] = useState(new Map());

  // Shipper config editor state
  const [showConfig, setShowConfig] = useState(false);
  const [cfgForm, setCfgForm] = useState(null);
  const [cfgSaving, setCfgSaving] = useState(false);

  useEffect(() => {
    loadOrders();
    loadConfig();
    loadShipQueueErrors();
  }, []);

  async function loadShipQueueErrors() {
    try {
      const snap = await getDocs(
        query(collection(db, "shipping_queue"), where("status", "==", "api_error"))
      );
      const m = new Map();
      snap.docs.forEach((d) => {
        const data = d.data();
        if (data.orderId) m.set(data.orderId,
          `Als versendet markieren über die Etsy-Schnittstelle für #${data.orderId} fehlgeschlagen – ${data.errorMsg ? `Grund: ${data.errorMsg}` : "ein Grund wurde nicht aufgezeichnet"}. Der Scraper versucht es anschließend über die Etsy-Seite.`);
      });
      setShipQueueErrors(m);
    } catch { /* ignore */ }
  }

  async function loadOrders() {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, "etsy_orders"), where("workflowStage", "==", "druck"))
      );
      const list = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const lastName = name => (name || "").trim().split(" ").pop() || "";
          return lastName(a.buyerName).localeCompare(lastName(b.buyerName), "de");
        });
      setOrders(list);
    } catch (e) {
      setError("Bestellungen konnten nicht geladen werden: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadConfig() {
    try {
      const snap = await getDoc(doc(db, "config", "shippingConfig"));
      if (snap.exists()) setCfgForm(snap.data());
      else setCfgForm({
        name1: SHOP.name, name2: "",
        street: "", house: "", postalCode: "", city: "",
        billingNumber: "", sandboxMode: true,
      });
    } catch { /* ignore */ }
  }

  async function saveConfig() {
    setCfgSaving(true);
    try {
      await setDoc(doc(db, "config", "shippingConfig"), cfgForm);
      setShowConfig(false);
    } catch (e) {
      alert("Fehler beim Speichern: " + e.message);
    } finally {
      setCfgSaving(false);
    }
  }

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll(filteredOrders) {
    const filteredIds = filteredOrders.map((o) => o.id);
    const allFilteredSelected = filteredIds.every((id) => selected.has(id));
    if (allFilteredSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }

  function proceedToReview() {
    const rows = orders
      .filter((o) => selected.has(o.id))
      .map((o) => {
        const { street, house } = splitStreet(o.address?.street || "");
        return {
          orderId: o.id,
          buyerName: o.buyerName || "",
          items: o.items || [],
          shipByDate: o.shipByDate,
          name: o.buyerName || "",
          street,
          house,
          // Optional address line 2 — used for German "OT / Ortsteil"
          // additions (e.g. "OT Siebigerode") or "c/o" or building/floor.
          // Passed to DHL as `additionalAddressInformation1`.
          addressLine2: o.address?.addressLine2 || "",
          postalCode: o.address?.postalCode || "",
          city: o.address?.city || "",
          weightKg: suggestWeight(o.items || []),
        };
      });
    setShipRows(rows);
    setStep("review");
  }

  function updateRow(idx, field, value) {
    setShipRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  }

  function setAllWeights(kg) {
    setShipRows((prev) => prev.map((r) => ({ ...r, weightKg: kg })));
  }

  async function generateLabels() {
    setGenerating(true);
    setError("");
    try {
      const fn = httpsCallable(getFunctions(undefined, "europe-west1"), "createShippingLabels");
      const payload = shipRows.map((r) => ({
        orderId: r.orderId,
        consignee: {
          name: r.name,
          street: r.street,
          house: r.house,
          addressLine2: r.addressLine2 || "",
          postalCode: r.postalCode,
          city: r.city,
        },
        weightKg: Number(r.weightKg),
      }));
      const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString();
      const res = await fn({ shipments: payload, scheduledAt });
      setResults(res.data.results || []);
      setSandboxMode(res.data.sandboxMode || false);
      setStep("done");
    } catch (e) {
      setError("Fehler bei der Etikettenerstellung: " + (e.message || String(e)));
    } finally {
      setGenerating(false);
    }
  }

  async function generatePackzettel() {
    const selectedOrders = orders.filter((o) => selected.has(o.id));
    if (selectedOrders.length === 0) return;

    // Etsy private notes (shopNotes) were previously scraped here per-order
    // before PDF generation — see git history 2026-08-06 for that path.
    // Removed 2026-08-11 on user request: scrape took 22-35s per order
    // (10 orders = 4-6 min wait) and CAPTCHA-risk was high. In practice
    // Alexandra rarely needs the notes on the Packzettel itself. PDF is
    // now built directly from Firestore data — instant.
    const ordersWithNotes = selectedOrders;

    // ── Build PDF ─────────────────────────────────────────────────────────
    const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");

    const pdfDoc = await PDFDocument.create();
    const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontBold    = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    const A4_W = 595.28;
    const A4_H = 841.89;
    const MARGIN = 50;
    const LINE = 16;

    // Replace characters outside WinAnsi to avoid encoding errors.
    // Map known symbols first, then fall back to "?" for anything else.
    const safe = (s) => (s || "")
      .replace(/\u2713|\u2714/g, "(ok)")   // ✓ ✔
      .replace(/\u2717|\u2718/g, "(x)")    // ✗ ✘
      .replace(/\u2019/g, "'")             // ' right single quote
      .replace(/\u201c|\u201e/g, '"')      // " „ open quotes
      .replace(/\u201d/g, '"')             // " close quote
      .replace(/\u2026/g, "...")           // … ellipsis
      .replace(/\u2013/g, "-")            // – en dash
      .replace(/\u2014/g, "--")           // — em dash
      // eslint-disable-next-line no-control-regex
      .replace(/[^\x00-\xFF]/g, "?");

    for (const order of ordersWithNotes) {
      const page = pdfDoc.addPage([A4_W, A4_H]);
      let y = A4_H - MARGIN;

      const draw = (text, {
        x = MARGIN, size = 10,
        font = fontRegular,
        color = rgb(0, 0, 0),
      } = {}) => {
        page.drawText(safe(text), { x, y, size, font, color });
        y -= LINE;
      };

      // Word-wrap a single string across multiple lines
      const drawWrapped = (text, {
        x = MARGIN, size = 10,
        font = fontRegular,
        color = rgb(0, 0, 0),
      } = {}) => {
        const maxW = A4_W - x - MARGIN;
        // Approximate: Helvetica avg glyph width ≈ 0.52 × size (in points)
        const maxChars = Math.floor(maxW / (size * 0.52));
        const words = (text || "").split(" ");
        let line = "";
        for (const word of words) {
          if (line && (line + " " + word).length > maxChars) {
            page.drawText(safe(line), { x, y, size, font, color });
            y -= LINE;
            line = word;
          } else {
            line = line ? `${line} ${word}` : word;
          }
        }
        if (line) {
          page.drawText(safe(line), { x, y, size, font, color });
          y -= LINE;
        }
      };

      // Render multi-line text (respects \n, word-wraps each segment)
      const drawMultiline = (text, opts = {}) => {
        const rawLines = (text || "").split(/\r?\n/);
        for (const rawLine of rawLines) {
          if (!rawLine.trim()) {
            y -= LINE * 0.5; // blank line = half gap
          } else {
            drawWrapped(rawLine, opts);
          }
        }
      };

      const drawRule = () => {
        y -= 4;
        page.drawLine({
          start: { x: MARGIN, y },
          end:   { x: A4_W - MARGIN, y },
          thickness: 0.5,
          color: rgb(0.8, 0.8, 0.8),
        });
        y -= 10;
      };

      // ── Header ────────────────────────────────────────────────────
      draw(`Packzettel  #${order.id}`, { size: 16, font: fontBold });
      y -= 2;
      draw(
        `Bestelldatum: ${order.orderDate || "—"}    Versand bis: ${order.shipByDate || "—"}`,
        { size: 9, color: rgb(0.45, 0.45, 0.45) },
      );
      drawRule();

      // ── Recipient ─────────────────────────────────────────────────
      draw("Versand an", { size: 8, color: rgb(0.5, 0.5, 0.5) });
      draw(order.buyerName || "—", { font: fontBold, size: 11 });
      if (order.address?.street)
        draw(order.address.street, { size: 10 });
      if (order.address?.postalCode || order.address?.city)
        draw(
          `${order.address?.postalCode || ""} ${order.address?.city || ""}`.trim(),
          { size: 10 },
        );
      y -= 6;
      drawRule();

      // ── Items ─────────────────────────────────────────────────────
      draw("Artikel", { size: 8, color: rgb(0.5, 0.5, 0.5) });
      y -= 2;

      for (const item of (order.items || [])) {
        const qtyPrefix = item.quantity > 1 ? `${item.quantity}x  ` : "";
        // Title may be long — wrap it
        drawWrapped(`${qtyPrefix}${item.title || ""}`, { font: fontBold, size: 10 });

        if (item.variant) {
          drawWrapped(`Variante: ${item.variant}`, {
            x: MARGIN + 14, size: 9, color: rgb(0.3, 0.3, 0.3),
          });
        }

        if (item.personalization) {
          // Label on its own line, then the text (respecting \n line breaks)
          draw("Personalisierung:", {
            x: MARGIN + 14, size: 9, color: rgb(0.1, 0.4, 0.1),
          });
          drawMultiline(item.personalization, {
            x: MARGIN + 14, size: 9, color: rgb(0.1, 0.4, 0.1),
          });
        }
        y -= 4;
      }

      // ── Buyer note ────────────────────────────────────────────────
      if (order.noteFromBuyer) {
        drawRule();
        draw("Notiz des Käufers", { size: 8, color: rgb(0.5, 0.5, 0.5) });
        drawMultiline(order.noteFromBuyer, { size: 9 });
      }

      // ── Footer ────────────────────────────────────────────────────
      page.drawText(
        safe(`Erstellt: ${new Date().toLocaleString("de-DE")}`),
        {
          x: MARGIN, y: MARGIN - 20,
          size: 7, font: fontRegular, color: rgb(0.7, 0.7, 0.7),
        },
      );
    }

    const uri = await pdfDoc.saveAsBase64({ dataUri: true });
    const a = document.createElement("a");
    a.href = uri;
    a.download = `packzettel_${new Date().toISOString().slice(0, 10)}.pdf`;
    a.click();
  }

  async function downloadMergedPDF() {
    const labelsWithData = results.filter((r) => r.success && r.labelB64);
    if (labelsWithData.length === 0) {
      alert("Keine Label-PDFs vorhanden (Sandbox-Modus liefert keine echten PDFs).");
      return;
    }
    try {
      const merged = await PDFDocument.create();
      for (const r of labelsWithData) {
        const bytes = Uint8Array.from(atob(r.labelB64), (c) => c.charCodeAt(0));
        const srcDoc = await PDFDocument.load(bytes);
        const pages = await merged.copyPages(srcDoc, srcDoc.getPageIndices());
        pages.forEach((p) => merged.addPage(p));
      }
      const uri = await merged.saveAsBase64({ dataUri: true });
      const a = document.createElement("a");
      a.href = uri;
      a.download = `versandetiketten_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
    } catch (e) {
      alert("PDF-Erstellung fehlgeschlagen: " + e.message);
    }
  }

  // ─── Derived state ───────────────────────────────────────────────────────────

  const sq = searchQuery.trim().toLowerCase();
  const filteredOrders = orders.filter((o) => {
    if (labelFilter === "no-label" && o.trackingNumber) return false;
    if (!sq) return true;
    return (
      (o.buyerName || "").toLowerCase().includes(sq) ||
      (o.id || "").toLowerCase().includes(sq) ||
      (o.items || []).some((it) => (it.title || "").toLowerCase().includes(sq))
    );
  });
  const allFilteredSelected =
    filteredOrders.length > 0 &&
    filteredOrders.every((o) => selected.has(o.id));

  // Packzettel is instant now — no notes-fetching step.
  const packzettelLabel = "Packzettel";

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5em", gap: "1em", flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: "1.3rem", color: "#1a202c" }}>
          Versandetiketten
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75em", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
            <span style={{ fontSize: "0.82rem", color: "#4a5568", fontWeight: "600", whiteSpace: "nowrap" }}>
              Versandzeitpunkt:
            </span>
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              style={{ ...inputStyle, width: "auto", padding: "0.35em 0.5em", fontSize: "0.82rem" }}
            />
            <input
              type="time"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              style={{ ...inputStyle, width: "7em", padding: "0.35em 0.5em", fontSize: "0.82rem" }}
            />
          </div>
          <button
            onClick={() => setShowConfig((v) => !v)}
            style={{ ...btnSecondary, fontSize: "0.8rem", padding: "0.4em 0.9em" }}
          >
            ⚙ Absender-Konfiguration
          </button>
        </div>
      </div>

      {/* ── Shipper config editor ── */}
      {showConfig && cfgForm && (
        <div style={{ ...card, border: "1px solid #e9d8fd", background: "#faf5ff" }}>
          <h3 style={{ margin: "0 0 1em", fontSize: "1rem", color: "#553c9a" }}>
            Absender-Konfiguration (DHL)
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75em 1em" }}>
            {[
              ["name1", "Firma / Name"],
              ["name2", "Name Zeile 2 (optional)"],
              ["street", "Straße"],
              ["house", "Hausnr."],
              ["postalCode", "PLZ"],
              ["city", "Stadt"],
              ["billingNumber", "DHL Abrechnungsnummer (14-stellig)"],
            ].map(([field, label]) => (
              <label key={field} style={{ display: "flex", flexDirection: "column", gap: "0.25em", fontSize: "0.85rem", color: "#4a5568" }}>
                {label}
                <input
                  style={inputStyle}
                  value={cfgForm[field] || ""}
                  onChange={(e) => setCfgForm((f) => ({ ...f, [field]: e.target.value }))}
                />
              </label>
            ))}
            <label style={{ display: "flex", alignItems: "center", gap: "0.5em", fontSize: "0.85rem", color: "#4a5568", gridColumn: "1 / -1" }}>
              <input
                type="checkbox"
                checked={!!cfgForm.sandboxMode}
                onChange={(e) => setCfgForm((f) => ({ ...f, sandboxMode: e.target.checked }))}
                style={{ accentColor: "#553c9a", width: 15, height: 15 }}
              />
              Sandbox-Modus (validate=true — keine echten Labels, zum Testen)
            </label>
          </div>
          <div style={{ display: "flex", gap: "0.75em", marginTop: "1em" }}>
            <button style={btnPrimary} onClick={saveConfig} disabled={cfgSaving}>
              {cfgSaving ? "Speichern…" : "Speichern"}
            </button>
            <button style={btnSecondary} onClick={() => setShowConfig(false)}>Abbrechen</button>
          </div>
        </div>
      )}

      {error && (
        <div style={{ background: "#fff5f5", border: "1px solid #fed7d7", borderRadius: "8px", padding: "0.75em 1em", marginBottom: "1em", color: "#c53030", fontSize: "0.9rem" }}>
          {error}
        </div>
      )}

      {/* ── Step 1: Order selection ── */}
      {step === "select" && (
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75em" }}>
            <h3 style={{ margin: 0, fontSize: "1rem", color: "#2d3748" }}>
              Bestellungen in &quot;Druck&quot; ({orders.length})
              {orders.filter((o) => o.doNotShipBefore || o.shippingNote).length > 0 && (
                <span style={{
                  fontSize: "0.85rem", color: "#c05621",
                  marginLeft: "0.75em", fontWeight: "500",
                }}>
                  · davon mit Hinweisen ({orders.filter((o) => o.doNotShipBefore || o.shippingNote).length})
                </span>
              )}
            </h3>
            <div style={{ display: "flex", gap: "0.5em", alignItems: "center" }}>
              {filteredOrders.length > 0 && (
                <button
                  style={{ ...btnSecondary, fontSize: "0.8rem", padding: "0.35em 0.8em" }}
                  onClick={() => toggleAll(filteredOrders)}
                >
                  {allFilteredSelected ? "Alle abwählen" : "Alle auswählen"}
                </button>
              )}
              <button
                style={{
                  ...btnSecondary,
                  fontSize: "0.85rem", padding: "0.4em 1em",
                  opacity: selected.size === 0 ? 0.5 : 1,
                  minWidth: "9em",
                }}
                disabled={selected.size === 0}
                onClick={generatePackzettel}
              >
                {packzettelLabel}
              </button>
              <button
                style={{ ...btnPrimary, opacity: selected.size === 0 ? 0.5 : 1, fontSize: "0.85rem", padding: "0.4em 1em" }}
                disabled={selected.size === 0}
                onClick={proceedToReview}
              >
                Weiter ({selected.size}) →
              </button>
            </div>
          </div>

          {/* Search + label filter */}
          {orders.length > 0 && (
            <div style={{ marginBottom: "0.75em", display: "flex", flexDirection: "column", gap: "0.5em" }}>
              <div style={{ position: "relative" }}>
                <input
                  type="search"
                  placeholder="Suche nach Name, Bestellnummer, Artikel …"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: "0.45em 0.75em 0.45em 2em",
                    border: "1px solid #e2e8f0", borderRadius: "8px",
                    fontSize: "0.875rem", color: "#2d3748", background: "#fff",
                  }}
                />
                <span style={{
                  position: "absolute", left: "0.6em", top: "50%",
                  transform: "translateY(-50%)", fontSize: "0.85rem",
                  color: "#a0aec0", pointerEvents: "none",
                }}>🔍</span>
              </div>
              <div style={{ display: "flex", gap: "0.4em" }}>
                {[
                  { key: "all",      label: "Alle" },
                  { key: "no-label", label: "Ohne Etikett" },
                ].map((f) => {
                  const active = labelFilter === f.key;
                  return (
                    <button
                      key={f.key}
                      onClick={() => setLabelFilter(f.key)}
                      style={{
                        padding: "0.3em 0.85em", borderRadius: "20px",
                        border: active ? "2px solid #553c9a" : "1px solid #cbd5e0",
                        background: active ? "#e9d8fd" : "#fff",
                        color: active ? "#553c9a" : "#4a5568",
                        fontSize: "0.8rem", fontWeight: active ? "700" : "500",
                        cursor: "pointer",
                      }}
                    >
                      {f.label}
                      {f.key === "no-label" && (
                        <span style={{ marginLeft: "0.4em", color: active ? "#553c9a" : "#a0aec0" }}>
                          ({orders.filter((o) => !o.trackingNumber).length})
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {loading ? (
            <p style={{ color: "#718096" }}>Lade Bestellungen…</p>
          ) : orders.length === 0 ? (
            <p style={{ color: "#718096" }}>Keine Bestellungen in der &quot;Druck&quot;-Phase.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
              <thead>
                <tr style={{ background: "#f7fafc", color: "#718096", textAlign: "left" }}>
                  <th style={{ padding: "0.6em 0.75em", width: 32 }}></th>
                  <th style={{ padding: "0.6em 0.75em" }}>Bestellung</th>
                  <th style={{ padding: "0.6em 0.75em" }}>Käufer</th>
                  <th style={{ padding: "0.6em 0.75em" }}>Artikel</th>
                  <th style={{ padding: "0.6em 0.75em" }}>Versand bis</th>
                  <th style={{ padding: "0.6em 0.75em" }}>Status</th>
                  <th style={{ padding: "0.6em 0.75em" }}>Etikett erstellt</th>
                  <th style={{ padding: "0.6em 0.75em" }}>Tracking-Nr.</th>
                  <th style={{ padding: "0.6em 0.75em" }}>Geplant</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() => toggleSelect(o.id)}
                    style={{
                      borderTop: "1px solid #e2e8f0",
                      cursor: "pointer",
                      background: selected.has(o.id) ? "#faf5ff" : "transparent",
                    }}
                  >
                    <td style={{ padding: "0.6em 0.75em" }}>
                      <input
                        type="checkbox"
                        checked={selected.has(o.id)}
                        onChange={() => toggleSelect(o.id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ accentColor: "#553c9a", width: 15, height: 15 }}
                      />
                    </td>
                    <td style={{ padding: "0.6em 0.75em", color: "#553c9a", fontWeight: 600 }}>
                      #{o.id}
                    </td>
                    <td style={{ padding: "0.6em 0.75em" }}>{o.buyerName || "–"}</td>
                    <td style={{ padding: "0.6em 0.75em", color: "#4a5568" }}>
                      {(o.items || []).map((it) => it.title).join(", ").slice(0, 60) || "–"}
                    </td>
                    <td style={{ padding: "0.6em 0.75em", whiteSpace: "nowrap" }}>
                      {formatDate(o.shipByDate)}
                    </td>
                    <td style={{ padding: "0.6em 0.75em" }}>
                      {o.labelCreatedAt ? (
                        <span style={{ background: "#c6f6d5", color: "#276749", padding: "0.2em 0.5em", borderRadius: "10px", fontSize: "0.75rem", fontWeight: 600 }}>
                          ✓ Etikett erstellt
                        </span>
                      ) : null}
                      {shipQueueErrors.has(o.id) && (
                        <span title={shipQueueErrors.get(o.id)}
                          style={{ background: "#fed7d7", color: "#c53030", padding: "0.2em 0.5em", borderRadius: "10px", fontSize: "0.75rem", fontWeight: 600, display: "inline-block", marginTop: o.labelCreatedAt ? "0.3em" : 0, cursor: "help" }}>
                          ⚠ Versand-Fehler
                        </span>
                      )}
                      {(o.doNotShipBefore || o.shippingNote) && (
                        <span
                          title={[
                            o.doNotShipBefore
                              ? `Nicht vor: ${formatDate(o.doNotShipBefore)}`
                              : "",
                            o.shippingNote || "",
                          ].filter(Boolean).join(" – ")}
                          style={{
                            background: "#fffbeb", color: "#92400e",
                            border: "1px solid #fbbf24",
                            padding: "0.2em 0.5em", borderRadius: "10px",
                            fontSize: "0.75rem", fontWeight: 600,
                            display: "inline-block", cursor: "default",
                            marginTop: (o.labelCreatedAt || shipQueueErrors.has(o.id))
                              ? "0.3em" : 0,
                          }}
                        >
                          ⚠ Achtung
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "0.6em 0.75em", whiteSpace: "nowrap", fontSize: "0.82rem", color: "#4a5568" }}>
                      {o.labelCreatedAt
                        ? (o.labelCreatedAt.toDate
                            ? o.labelCreatedAt.toDate().toLocaleDateString("de-DE")
                            : formatDate(o.labelCreatedAt))
                        : "–"}
                    </td>
                    <td style={{ padding: "0.6em 0.75em", fontFamily: "monospace", fontSize: "0.78rem", color: "#4a5568" }}>
                      {o.trackingNumber || "–"}
                    </td>
                    <td style={{ padding: "0.6em 0.75em", whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                      {o.scheduledShippingAt ? (
                        <span style={{ color: "#553c9a", fontWeight: 600 }}>
                          {formatScheduled(o.scheduledShippingAt)}
                        </span>
                      ) : (
                        <span style={{ color: "#cbd5e0" }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ marginTop: "1.25em", display: "flex", justifyContent: "flex-end", gap: "0.5em" }}>
            <button
              style={{
                ...btnSecondary,
                opacity: selected.size === 0 ? 0.5 : 1,
                minWidth: "9em",
              }}
              disabled={selected.size === 0}
              onClick={generatePackzettel}
            >
              {packzettelLabel}
            </button>
            <button
              style={{ ...btnPrimary, opacity: selected.size === 0 ? 0.5 : 1 }}
              disabled={selected.size === 0}
              onClick={proceedToReview}
            >
              Weiter ({selected.size} ausgewählt) →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 2: Address verification + weight ── */}
      {step === "review" && (
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1em", flexWrap: "wrap", gap: "0.5em" }}>
            <h3 style={{ margin: 0, fontSize: "1rem", color: "#2d3748" }}>
              Versanddetails prüfen ({shipRows.length} Bestellungen)
            </h3>
            <div style={{ display: "flex", alignItems: "center", gap: "0.4em" }}>
              <span style={{ fontSize: "0.78rem", color: "#718096", marginRight: "0.2em" }}>Alle:</span>
              {[1, 2].map((kg) => (
                <button
                  key={kg}
                  onClick={() => setAllWeights(kg)}
                  style={{
                    padding: "0.3em 0.85em", borderRadius: "6px", border: "1px solid #cbd5e0",
                    background: "#f7fafc", color: "#2d3748", fontWeight: "600",
                    fontSize: "0.8rem", cursor: "pointer",
                  }}
                >
                  {kg} kg
                </button>
              ))}
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem", minWidth: 720 }}>
              <thead>
                <tr style={{ background: "#f7fafc", color: "#718096", textAlign: "left" }}>
                  <th style={{ padding: "0.5em 0.6em" }}>Bestellung</th>
                  <th style={{ padding: "0.5em 0.6em" }}>Empfänger</th>
                  <th style={{ padding: "0.5em 0.6em" }}>Straße</th>
                  <th style={{ padding: "0.5em 0.6em", width: 80 }}>Hausnr.</th>
                  <th style={{ padding: "0.5em 0.6em", width: 130 }} title="Adresszusatz — z.B. OT / Ortsteil, c/o, Etage">Zusatz</th>
                  <th style={{ padding: "0.5em 0.6em", width: 80 }}>PLZ</th>
                  <th style={{ padding: "0.5em 0.6em" }}>Stadt</th>
                  <th style={{ padding: "0.5em 0.6em", width: 110 }}>Gewicht</th>
                </tr>
              </thead>
              <tbody>
                {shipRows.map((r, idx) => (
                  <tr key={r.orderId} style={{ borderTop: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "0.5em 0.6em", color: "#553c9a", fontWeight: 600, whiteSpace: "nowrap" }}>
                      #{r.orderId}
                    </td>
                    <td style={{ padding: "0.5em 0.4em" }}>
                      <input style={inputStyle} value={r.name} onChange={(e) => updateRow(idx, "name", e.target.value)} />
                    </td>
                    <td style={{ padding: "0.5em 0.4em" }}>
                      <input style={inputStyle} value={r.street} onChange={(e) => updateRow(idx, "street", e.target.value)} />
                    </td>
                    <td style={{ padding: "0.5em 0.4em" }}>
                      <input
                        style={{ ...inputStyle, borderColor: !r.house ? "#fc8181" : "#cbd5e0" }}
                        value={r.house}
                        onChange={(e) => updateRow(idx, "house", e.target.value)}
                        placeholder="!"
                      />
                    </td>
                    <td style={{ padding: "0.5em 0.4em" }}>
                      <input
                        style={{
                          ...inputStyle,
                          background: r.addressLine2 ? "#fefcbf" : "#ffffff",
                        }}
                        value={r.addressLine2}
                        onChange={(e) => updateRow(idx, "addressLine2", e.target.value)}
                        placeholder="—"
                        title="Adresszusatz (optional, z.B. OT Siebigerode, c/o, Etage)"
                      />
                    </td>
                    <td style={{ padding: "0.5em 0.4em" }}>
                      <input style={inputStyle} value={r.postalCode} onChange={(e) => updateRow(idx, "postalCode", e.target.value)} />
                    </td>
                    <td style={{ padding: "0.5em 0.4em" }}>
                      <input style={inputStyle} value={r.city} onChange={(e) => updateRow(idx, "city", e.target.value)} />
                    </td>
                    <td style={{ padding: "0.5em 0.4em" }}>
                      <select
                        style={{ ...inputStyle, cursor: "pointer" }}
                        value={r.weightKg}
                        onChange={(e) => updateRow(idx, "weightKg", Number(e.target.value))}
                      >
                        {WEIGHTS.map((w) => (
                          <option key={w} value={w}>{w} kg</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {shipRows.some((r) => !r.house) && (
            <p style={{ color: "#c53030", fontSize: "0.82rem", marginTop: "0.75em" }}>
              ⚠ Einige Hausnummern fehlen (rot markiert) — bitte vor dem Erstellen ergänzen.
            </p>
          )}

          <div style={{ marginTop: "1.25em", display: "flex", gap: "0.75em", justifyContent: "flex-end" }}>
            <button style={btnSecondary} onClick={() => setStep("select")} disabled={generating}>
              ← Zurück
            </button>
            <button
              style={{ ...btnPrimary, opacity: (generating || shipRows.some((r) => !r.house)) ? 0.6 : 1 }}
              disabled={generating || shipRows.some((r) => !r.house)}
              onClick={generateLabels}
            >
              {generating ? "Erstelle Etiketten…" : `Etiketten erstellen (${shipRows.length})`}
            </button>
          </div>
        </div>
      )}

      {/* ── Step 3: Done ── */}
      {step === "done" && (
        <div style={card}>
          <h3 style={{ margin: "0 0 0.5em", fontSize: "1rem", color: "#2d3748" }}>
            {results.filter((r) => r.success).length} Etikett(e) erstellt
            {sandboxMode && (
              <span style={{ marginLeft: "0.75em", background: "#fefcbf", color: "#744210", padding: "0.2em 0.6em", borderRadius: "10px", fontSize: "0.75rem", fontWeight: 600 }}>
                Sandbox-Modus
              </span>
            )}
          </h3>
          {sandboxMode && (
            <p style={{ color: "#744210", fontSize: "0.85rem", marginBottom: "1em" }}>
              Im Sandbox-Modus werden keine echten Labels erstellt und keine PDF-Daten zurückgegeben. Deaktiviere den Sandbox-Modus in der Konfiguration für echte Etiketten.
            </p>
          )}

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem", marginBottom: "1.25em" }}>
            <thead>
              <tr style={{ background: "#f7fafc", color: "#718096", textAlign: "left" }}>
                <th style={{ padding: "0.5em 0.75em" }}>Bestellung</th>
                <th style={{ padding: "0.5em 0.75em" }}>Tracking-Nummer</th>
                <th style={{ padding: "0.5em 0.75em" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.orderId} style={{ borderTop: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "0.5em 0.75em", color: "#553c9a", fontWeight: 600 }}>#{r.orderId}</td>
                  <td style={{ padding: "0.5em 0.75em", fontFamily: "monospace" }}>
                    {r.trackingNumber || (sandboxMode ? "–" : "–")}
                  </td>
                  <td style={{ padding: "0.5em 0.75em" }}>
                    {r.success ? (
                      <span style={{ color: "#276749", fontWeight: 600 }}>✓ OK</span>
                    ) : (
                      <span style={{ color: "#c53030" }}>✗ {r.error}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: "flex", gap: "0.75em" }}>
            {!sandboxMode && results.some((r) => r.success && r.labelB64) && (
              <button style={btnPrimary} onClick={downloadMergedPDF}>
                ↓ Alle Labels herunterladen (PDF)
              </button>
            )}
            <button style={btnSecondary} onClick={() => { setStep("select"); loadOrders(); setResults([]); setSelected(new Set()); }}>
              Zurück zur Auswahl
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
